/**
 * Lo que comparten la pantalla de Inicio y su versión en PDF.
 *
 * Existe para que las dos NO puedan decir cosas distintas: el mismo veredicto,
 * las mismas palabras, los mismos colores y la misma forma de leer las fechas.
 * Si el PDF armara su propio texto, el día que alguien ajuste una frase en la
 * pantalla el documento que el ejecutivo descargó diría otra cosa.
 */

export const TONO = { sigue: '#34d399', revisa: '#fbbf24', detente: '#f87171' };
export const TONO_SUAVE = {
  sigue: 'rgba(52,211,153,0.10)', revisa: 'rgba(251,191,36,0.10)', detente: 'rgba(248,113,113,0.10)',
};
// Para papel: los mismos tonos, oscurecidos para que se lean sobre blanco.
export const TONO_PAPEL = { sigue: '#047857', revisa: '#b45309', detente: '#b91c1c' };

export const TITULO = {
  sigue: 'Vas bien — sigue con tu jornada',
  revisa: 'Revisa antes de seguir',
  detente: 'Detente y revisa tus llamadas',
};
export const NOMBRE_SENAL = { sigue: 'Sigue', revisa: 'Revisa', detente: 'Detente' };

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const DIAS_LARGO = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];

// 'YYYY-MM-DD' a fecha local SIN pasar por UTC: `new Date('2026-09-22')` es
// medianoche UTC, que en México es el día anterior a las 18:00.
export const fecha = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };
export const diaSemana = (iso) => DIAS[fecha(iso).getDay()];
export const corta = (iso) => { const f = fecha(iso); return `${DIAS[f.getDay()]} ${f.getDate()} ${MESES[f.getMonth()]}`; };
export const larga = (iso) => { const f = fecha(iso); return `${DIAS_LARGO[f.getDay()]} ${f.getDate()} de ${MESES_LARGO[f.getMonth()]}`; };
export const f1 = (v) => (v == null ? '—' : Number(v).toFixed(1));

export function primerNombre(texto) {
  const p = String(texto || '').trim().split(/\s+/)[0] || '';
  return p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : '';
}

// Cuántos días seguidos lleva bajando, contando el último. Es presentación, no
// una regla: sirve para decir "llevas tres días a la baja", no para colorear.
export function racha(tendencia) {
  let n = 1;
  for (let i = tendencia.length - 1; i > 0 && tendencia[i].nota < tendencia[i - 1].nota; i--) n++;
  return n;
}

/** El texto del veredicto. UNA redacción para la pantalla y el PDF. */
export function textoSenal(datos) {
  const { senal, ultimo_dia: ult, tendencia } = datos;
  const glob = datos.global;
  if (!ult) return null;
  const cuando = ult.es_ayer ? 'Ayer' : `Tu último día (${corta(ult.fecha)})`;
  const bajo = glob.nota != null && ult.nota < glob.nota;
  const baja = tendencia.length > 1 ? racha(tendencia) : 1;
  return {
    sigue: `${cuando} cerraste en ${f1(ult.nota)}${bajo ? ', un poco abajo de tu mes pero en verde' : ', arriba de tu promedio del mes'}. No hay nada que atender hoy.`,
    revisa: `${cuando} cerraste en ${f1(ult.nota)}${bajo ? `, por debajo de tu mes (${f1(glob.nota)})` : ''}. Te toma cinco minutos: abajo están las llamadas y el criterio que lo explican.`,
    detente: `${cuando} cerraste en ${f1(ult.nota)}${baja >= 2 ? ` y llevas ${baja} días a la baja` : ''}. Antes de tomar la siguiente llamada, revisa las de abajo.`,
  }[senal];
}

// Qué significa cada color, con los cortes que manda el servidor. Se escribe
// aquí una vez porque aparece en tres lugares (la gráfica del mes, su leyenda y
// el PDF), y los tres tienen que decir lo mismo cuando alguien mueva un corte.
export function rangos(cortes) {
  const antes = (v) => (Number.isInteger(v) ? v - 0.1 : v - 0.01).toFixed(1);
  return {
    sigue: `${f1(cortes.sigue)} o más`,
    revisa: `${f1(cortes.detente)} a ${antes(cortes.sigue)}`,
    detente: `menos de ${f1(cortes.detente)}`,
  };
}

// ── Los filtros ────────────────────────────────────────────────────────────
// Los estilos de los botones de filtro, compartidos por el Inicio y el
// Overview (`Filtros.jsx`): los mismos filtros tienen que verse iguales.
export const ACENTO = '#818cf8';
export const ROTULO = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5c5c6b',
};
export const CHIP = (on, color = ACENTO) => ({
  borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  cursor: 'pointer', whiteSpace: 'nowrap',
  color: on ? '#e6e6ec' : '#8b8b98',
  background: on ? 'rgba(129,140,248,0.18)' : 'rgba(255,255,255,0.04)',
  border: `1px solid ${on ? color : 'rgba(255,255,255,0.10)'}`,
});
// `tipo` y `venta` son listas desde el 28-sep-2026: se puede elegir más de uno
// a la vez, como en los pre-filtros de las vistas de admin.
export const SIN_FILTROS = { tipo: [], utilidad: null, venta: [], categoria: [], aprovechamiento: null };
// Con lo que abre el Inicio cuando no hay nada guardado (primera visita o
// recarga), decidido por Samuel el 28-sep-2026. OJO: aquí «No ventas» es todo
// lo que no es venta, así que Ventas + No ventas no recorta por venta y el
// default equivale a «todas las públicas».
export const FILTROS_INICIALES = { ...SIN_FILTROS, tipo: ['publica'], venta: ['ventas', 'no_ventas'] };
export const TIPOS = [['publica', 'Pública'], ['comercial', 'Comercial'], ['grupos', 'Grupos']];
export const UTILIDADES = [['util', 'Útiles'], ['no_util', 'No útiles']];
// "Venta" es la de todo el sistema (`sql_es_venta`): Pub - Venta y Com - Venta.
export const VENTAS = [['ventas', 'Ventas'], ['no_ventas', 'No ventas']];
export const APROVECHAMIENTOS = [['aprovechables', 'Sólo aprovechables'], ['no_aprovechables', 'No aprovechables']];

export const hayFiltros = (f) => Boolean(f.tipo?.length || f.utilidad || f.venta?.length || f.categoria?.length || f.aprovechamiento);

/** Los filtros como frase, para el PDF y para el aviso de "estás viendo...". */
export function describirFiltros(f) {
  const partes = [];
  const de = (lista, v) => lista.find(([k]) => k === v)?.[1];
  const varios = (lista, vs) => vs.map((v) => de(lista, v).toLowerCase()).join(' o ');
  if (f.tipo?.length) partes.push(`tipo ${varios(TIPOS, f.tipo)}`);
  if (f.utilidad) partes.push(de(UTILIDADES, f.utilidad).toLowerCase());
  if (f.venta?.length) partes.push(varios(VENTAS, f.venta));
  if (f.categoria?.length) partes.push(`categoría ${f.categoria.join(', ')}`);
  if (f.aprovechamiento) partes.push(de(APROVECHAMIENTOS, f.aprovechamiento).toLowerCase());
  return partes.length ? partes.join(' · ') : 'todas las llamadas entrantes calificadas';
}

/** Los filtros en la URL del PDF: el documento tiene que salir con lo mismo que
 *  la pantalla que lo pidió. */
export function filtrosAQuery(f) {
  const q = new URLSearchParams();
  (f.tipo || []).forEach((t) => q.append('tipo', t));
  if (f.utilidad) q.set('utilidad', f.utilidad);
  (f.venta || []).forEach((v) => q.append('venta', v));
  (f.categoria || []).forEach((c) => q.append('categoria', c));
  if (f.aprovechamiento) q.set('aprovechamiento', f.aprovechamiento);
  return q.toString();
}

export function filtrosDeQuery(search) {
  const q = new URLSearchParams(search);
  return {
    tipo: q.getAll('tipo'), utilidad: q.get('utilidad'), venta: q.getAll('venta'),
    categoria: q.getAll('categoria'), aprovechamiento: q.get('aprovechamiento'),
  };
}
